import { RoleList, isAdminRole } from './RoleManager';
import { SpotSyncAuth } from '../Data/Atoms';

// Permission types
export type Permission = string; // e.g., 'view-parker-details', 'edit-plan', etc.

// Claims structure for future extensibility
export interface Claims {
    roles: string[];
    customClaims?: Record<string, any>;
}

// Restricted routes - only these require specific role permissions
// Key is path pattern (use :id for facility ID, :parkerId for parker ID, etc.)
// Value is array of allowed role names (not identities)
// NOTE: Routes NOT listed here are accessible to ALL authenticated users (allow by default)
// NOTE: Only SuperAdmin bypasses route checks; OperatorAdmin is subject to ROUTE_PERMISSIONS (e.g. /admin is SuperAdmin-only).
export const ROUTE_PERMISSIONS: Record<string, string[]> = {
    // Admin-only routes
    '/operator': ['OperatorAdmin', 'SuperAdmin'],
    '/operator/facilities': ['FacilityUser'],
    '/operator/reports': ['FacilityUser'],
    '/operator/facility/create': ['OperatorAdmin', 'SuperAdmin'],
    '/admin': ['SuperAdmin'],
    
    // Facility tabs restricted from TransientReviewer (admins auto-allowed, no need to list)
    '/facility/:id/overview': ['FacilityUser'],
    '/facility/:id/listing': ['FacilityUser'],
    '/facility/:id/parker': ['FacilityUser'],
    '/facility/:id/plan': ['FacilityUser'],
    '/facility/:id/rate': ['FacilityUser'],
    '/facility/:id/finance': ['FacilityUser'],
    '/facility/:id/reports': ['FacilityUser'],
    // Note: /facility/:id/status is NOT listed here = accessible to ALL authenticated users
    
    // Parker details - restricted from TransientReviewer
    '/facility/:id/parker/:parkerId': ['FacilityUser'],
};

// Action permissions per role identity (uppercase)
// TODO: Replace with server-provided permissions in claims.customClaims.permissions
// This is temporary client-side fallback - will be deprecated when server sends permissions
export const ROLE_ACTION_PERMISSIONS: Record<string, Permission[]> = {
    [RoleList.FacilityUser.identity]: [
        'view-parker-details',
        'edit-parker',
        'view-plan',
        'edit-plan',
        'create-parker',
        'delete-parker',
        'view-reports',
        'manage-rates',
        'manage-finance',
        'manage-integrations',
        'facility-list-search',
        'can-reprint-invoice'
    ],
    [RoleList.TransientReviewer.identity]: [
        'view-session-details',
        'view-transient-list',
    ],
};

// Facility landing segments per role identity (uppercase)
export const ROLE_FACILITY_LANDING: Record<string, string> = {
    [RoleList.FacilityUser.identity]: 'overview',
    [RoleList.TransientReviewer.identity]: 'status',
};

// Extract claims from auth or JWT, for future use
export function extractClaims(auth?: SpotSyncAuth): Claims {
    if (!auth || !auth.account) {
        return { roles: [], customClaims: {} };
    }
    
    return {
        roles: auth.account.roles || auth.roles || [],
        customClaims: auth.claims || {}
    };
}

// Get role identities from role names
function getRoleIdentities(roles: string[]): string[] {
    return roles.map(roleName => {
        const roleKey = Object.keys(RoleList).find(
            key => RoleList[key as keyof typeof RoleList].name === roleName
        );
        return roleKey ? RoleList[roleKey as keyof typeof RoleList].identity : roleName.toUpperCase();
    });
}

// Match a path against a pattern (supports :id, :parkerId, etc.)
function matchPathPattern(pathname: string, pattern: string): boolean {
    // Convert pattern to regex: /facility/:id -> /facility/\d+
    const regexPattern = pattern
        .replace(/:[^\/]+/g, '\\d+')  // Replace :id, :parkerId etc with \d+
        .replace(/\//g, '\\/');        // Escape forward slashes
    
    const regex = new RegExp(`^${regexPattern}(\/.*)?$`);
    return regex.test(pathname);
}

// Find the most specific matching route pattern for a path
function findMatchingRoute(pathname: string): string | null {
    // Sort routes by specificity (longer = more specific)
    const sortedRoutes = Object.keys(ROUTE_PERMISSIONS).sort((a, b) => b.length - a.length);
    for (const route of sortedRoutes) {
        if (matchPathPattern(pathname, route)) {
            return route;
        }
    }
    
    return null;
}

// Check if user can access a path
export function canAccessPath(claims: Claims, pathname: string): boolean {
    if (!claims || !claims.roles || claims.roles.length === 0) {
        return false;
    }

    // SuperAdmin has access to everything (including /admin)
    if (claims.roles.includes(RoleList.SuperAdmin.name)) {
        return true;
    }

    // OperatorAdmin has access to everything except /admin (so they still get /operator, /facility/*, etc.)
    if (claims.roles.includes(RoleList.OperatorAdmin.name) && !pathname.startsWith('/admin')) {
        return true;
    }

    // Find matching route in restricted routes
    const matchingRoute = findMatchingRoute(pathname);
    
    // If no matching route found, ALLOW by default (permissive)
    // Only routes in ROUTE_PERMISSIONS are restricted
    if (!matchingRoute) {
        return true; // Allow access to unlisted routes
    }

    // Check if user has any of the allowed roles for this restricted route
    const allowedRoles = ROUTE_PERMISSIONS[matchingRoute];
    return claims.roles.some(role => allowedRoles.includes(role));
}

// Check if user can perform an action
export function canPerformAction(claims: Claims, action: Permission): boolean {
    if (!claims || !claims.roles || claims.roles.length === 0) {
        return false;
    }

    // Admins can perform all actions
    if (isAdminRole(claims.roles)) {
        return true;
    }

    // FUTURE: When server provides permissions, use them directly
    // Check if server provided permissions in customClaims
    if (claims.customClaims?.permissions && Array.isArray(claims.customClaims.permissions)) {
        return claims.customClaims.permissions.includes(action);
    }

    // FALLBACK: Use client-side role-based permissions (current behavior)
    const identities = getRoleIdentities(claims.roles);
    for (const identity of identities) {
        const permissions = ROLE_ACTION_PERMISSIONS[identity];
        if (permissions && permissions.includes(action)) {
            return true;
        }
    }

    return false;
}

// Get facility landing path for role
export function getFacilityLandingPath(claims: Claims, facilityId: number | string): string {

    if (!claims || !claims.roles || claims.roles.length === 0) {
        return `/facility/${facilityId}/overview`;
    }

    // Admins default to overview
    if (isAdminRole(claims.roles)) {
        return `/facility/${facilityId}/overview`;
    }

    const identities = getRoleIdentities(claims.roles);

    // Return landing for first matching role
    for (const identity of identities) {
        const segment = ROLE_FACILITY_LANDING[identity];
        if (segment) {
            return `/facility/${facilityId}/${segment}`;
        }
    }

    // Fallback
    return `/facility/${facilityId}/overview`;
}

// Get default redirect path based on claims
export function getDefaultRedirectPath(claims: Claims): string {
    if (!claims || !claims.roles || claims.roles.length === 0) {
        return '/';
    }

    // Use existing role redirect logic
    if (claims.roles.includes(RoleList.SuperAdmin.name)) {
        return '/admin/operators';
    } else if (claims.roles.includes(RoleList.OperatorAdmin.name)) {
        return '/operator/facilities';
    } else if (claims.roles.includes(RoleList.FacilityUser.name)) {
        return '/operator/facilities';
    }
    
    return '/';
}

